import { Checkbox, Colors, Text } from '@amelio/ui-library';

import { EVALUATION_TYPE_ICON_NAMES } from '../evaluationTypeIcons';
import { EvaluationTypeTile } from './EvaluationTypeTile/EvaluationTypeTile';

import type { EvaluationElementsSelection } from '../evaluationSelection';
import type { OptionProps } from '@amelio/ui-library';
import type { ReactNode } from 'react';

import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';

interface ManagerAssessmentTileProps {
    selection: EvaluationElementsSelection;
    /**
     * The cycle cannot change, or its Manager Assessment can only be read (the company lost
     * Performance PRO): the tile and both sub-options are shown as they are, not editable.
     */
    isFrozen: boolean;
    templateOptions: OptionProps[];
    noTemplateMessage: ReactNode;
    templateErrorMessage?: string;
    onToggle: () => void;
    onTemplateChange: (templateId: string) => void;
    onToggleOrganizationalValues: () => void;
}

/** The Manager Assessment tile of the Evaluation elements step, with its two sub-options. */
export const ManagerAssessmentTile = ({
    selection,
    isFrozen,
    templateOptions,
    noTemplateMessage,
    templateErrorMessage,
    onToggle,
    onTemplateChange,
    onToggleOrganizationalValues,
}: ManagerAssessmentTileProps) => {
    const { t } = useAmelioTranslations();
    const { getManagerLabel } = useCompanyInformation();

    return (
        <EvaluationTypeTile
            iconName={EVALUATION_TYPE_ICON_NAMES[EvaluationType.MANAGER_EVALUATION]}
            label={t('cycleEvaluationEvaluationElements.types.managerAssessment.label', {
                managerLabel: getManagerLabel(),
            })}
            description={t(
                'cycleEvaluationEvaluationElements.types.managerAssessment.description',
                {
                    managerLabel: getManagerLabel(),
                },
            )}
            isSelected={selection.managerAssessment}
            isReadOnly={isFrozen}
            templateOptions={templateOptions}
            templateId={selection.templateIds[EvaluationType.MANAGER_EVALUATION]}
            templateHint={t('cycleEvaluationEvaluationElements.template.managerHint', {
                managerLabel: getManagerLabel(),
            })}
            noTemplateMessage={noTemplateMessage}
            templateErrorMessage={templateErrorMessage}
            dataTestId={'evaluation-type-manager'}
            onToggle={onToggle}
            onTemplateChange={onTemplateChange}
        >
            <div className={'flex flex-wrap gap-x-8 gap-y-3'}>
                <div className={'flex items-center gap-2'}>
                    <Checkbox
                        checked={true}
                        disabled={true}
                        label={t('cycleEvaluationEvaluationElements.subOptions.assessCompetencies')}
                        dataTestId={'assess-competencies-checkbox'}
                    />

                    <Text
                        size={'small'}
                        color={Colors.BASE_400}
                    >
                        {`(${t('cycleEvaluationEvaluationElements.subOptions.required')})`}
                    </Text>
                </div>

                <Checkbox
                    checked={selection.assessOrganizationalValues}
                    readOnly={isFrozen}
                    label={t(
                        'cycleEvaluationEvaluationElements.subOptions.assessOrganizationalValues',
                    )}
                    onChange={onToggleOrganizationalValues}
                    dataTestId={'assess-organizational-values-checkbox'}
                />
            </div>
        </EvaluationTypeTile>
    );
};
